import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

import {
    Bell,
    BellOff,
    CircleCheck,
    Info,
    Pause,
    Play,
    RotateCcw,
    Timer
} from 'lucide-react'

import { cn } from '@/lib/utils'

// Session lengths in minutes. Basso et al. (2019, Behavioural Brain Research)
// saw benefits from 13 minutes a day after 8 weeks, so 10 and 15 bracket the
// dose that has been studied; shorter options are there for busy days.
const DURATIONS = [1, 3, 5, 10, 15, 20, 30]

const SETTLE_OPTIONS = [
    { seconds: 0, label: 'None' },
    { seconds: 30, label: '30 sec' },
    { seconds: 60, label: '1 min' }
]

const INTERVAL_OPTIONS = [
    { minutes: 0, label: 'Off' },
    { minutes: 1, label: 'Every minute' },
    { minutes: 2, label: 'Every 2 min' },
    { minutes: 5, label: 'Every 5 min' }
]

interface Anchor {
    key: string
    name: string
    prompt: string
    wander: string
}

const ANCHORS: Anchor[] = [
    {
        key: 'breath',
        name: 'Breath',
        prompt: 'Notice the breath where you feel it most: the nose, the chest or the belly.',
        wander: 'When your mind wanders, notice where it went, then come back to the next breath.'
    },
    {
        key: 'sounds',
        name: 'Sounds',
        prompt: 'Let sounds come and go. Notice them without naming or judging them.',
        wander: 'When you get caught in a thought, come back to whatever you can hear right now.'
    },
    {
        key: 'body',
        name: 'Body',
        prompt: 'Rest your attention on the feeling of your body sitting: the weight, the contact, the warmth.',
        wander: 'When your attention drifts, return to the places where your body touches the seat or floor.'
    }
]

type Status = 'idle' | 'running' | 'paused' | 'done'

const formatClock = (ms: number): string => {
    const total = Math.max(0, Math.ceil(ms / 1000))
    const minutes = Math.floor(total / 60)
    const seconds = total % 60

    return `${minutes}:${String(seconds).padStart(2, '0')}`
}

// A soft synthesised bell, so the tool needs no audio files. A few partials
// with a slow exponential fade read as a struck bowl rather than a beep.
const strikeBell = (context: AudioContext, at: number, volume = 0.22) => {
    const partials = [
        { ratio: 1, gain: 1, decay: 5 },
        { ratio: 2.76, gain: 0.35, decay: 3 },
        { ratio: 5.4, gain: 0.12, decay: 1.6 }
    ]

    const master = context.createGain()
    master.gain.value = volume
    master.connect(context.destination)

    for (const partial of partials) {
        const oscillator = context.createOscillator()
        const gain = context.createGain()

        oscillator.type = 'sine'
        oscillator.frequency.value = 432 * partial.ratio
        gain.gain.setValueAtTime(0.0001, at)
        gain.gain.exponentialRampToValueAtTime(partial.gain, at + 0.015)
        gain.gain.exponentialRampToValueAtTime(0.0001, at + partial.decay)

        oscillator.connect(gain)
        gain.connect(master)
        oscillator.start(at)
        oscillator.stop(at + partial.decay + 0.1)
    }
}

export const MeditationTimer: FC = (): ReactNode => {
    const [minutes, setMinutes] = useState(5)
    const [settleSeconds, setSettleSeconds] = useState(30)
    const [intervalMinutes, setIntervalMinutes] = useState(0)
    const [anchorKey, setAnchorKey] = useState(ANCHORS[0].key)
    const [sound, setSound] = useState(true)
    const [status, setStatus] = useState<Status>('idle')
    const [elapsed, setElapsed] = useState(0)

    const elapsedRef = useRef(0)
    const audioRef = useRef<AudioContext | null>(null)
    // Bells already rung this session, keyed by the moment they belong to, so
    // pausing and resuming never rings the same bell twice.
    const rungRef = useRef<Set<string>>(new Set())

    const anchor = ANCHORS.find((item) => item.key === anchorKey) ?? ANCHORS[0]
    const settleMs = settleSeconds * 1000
    const meditateMs = minutes * 60000
    const totalMs = settleMs + meditateMs

    const ring = (kind: 'start' | 'interval' | 'end') => {
        const context = audioRef.current
        if (!sound || !context) return

        const now = context.currentTime + 0.05
        if (kind === 'end') {
            strikeBell(context, now)
            strikeBell(context, now + 2.2)
            strikeBell(context, now + 4.4)
        } else {
            strikeBell(context, now, kind === 'interval' ? 0.14 : 0.22)
        }
    }

    // Keep the latest `ring` for the timer loop without restarting it whenever
    // the sound setting changes mid-session.
    const ringRef = useRef(ring)
    ringRef.current = ring

    useEffect(() => {
        if (status !== 'running') return

        const origin = performance.now() - elapsedRef.current

        // An interval rather than animation frames, so bells still ring when
        // the screen is locked or the tab is in the background.
        const tick = () => {
            const next = Math.min(performance.now() - origin, totalMs)
            const rung = rungRef.current

            if (next >= settleMs && !rung.has('start')) {
                rung.add('start')
                ringRef.current('start')
            }

            if (intervalMinutes > 0 && next > settleMs) {
                const intervalMs = intervalMinutes * 60000
                const count = Math.floor((next - settleMs) / intervalMs)
                for (let index = 1; index <= count; index++) {
                    const key = `interval-${index}`
                    // The closing bell covers the final moment on its own.
                    if (settleMs + index * intervalMs >= totalMs || rung.has(key)) continue
                    rung.add(key)
                    ringRef.current('interval')
                }
            }

            elapsedRef.current = next
            setElapsed(next)

            if (next >= totalMs) {
                rung.add('end')
                ringRef.current('end')
                setStatus('done')
            }
        }

        tick()
        const handle = window.setInterval(tick, 250)
        return () => window.clearInterval(handle)
    }, [status, totalMs, settleMs, intervalMinutes])

    // Keep the screen awake during a session where the browser allows it.
    useEffect(() => {
        if (status !== 'running' || !('wakeLock' in navigator)) return

        let sentinel: WakeLockSentinel | null = null
        let cancelled = false

        navigator.wakeLock
            .request('screen')
            .then((lock) => {
                if (cancelled) lock.release().catch(() => undefined)
                else sentinel = lock
            })
            .catch(() => undefined)

        return () => {
            cancelled = true
            sentinel?.release().catch(() => undefined)
        }
    }, [status])

    useEffect(() => () => {
        audioRef.current?.close().catch(() => undefined)
    }, [])

    const reset = () => {
        elapsedRef.current = 0
        rungRef.current = new Set()
        setElapsed(0)
        setStatus('idle')
    }

    // Browsers only allow sound after a tap, so the audio is unlocked here.
    const unlockAudio = () => {
        if (typeof window === 'undefined') return
        const AudioCtor = window.AudioContext
            ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
        if (!AudioCtor) return

        if (!audioRef.current) audioRef.current = new AudioCtor()
        audioRef.current.resume().catch(() => undefined)
    }

    const toggle = () => {
        if (status === 'running') {
            setStatus('paused')
            return
        }

        unlockAudio()

        if (status === 'done') {
            elapsedRef.current = 0
            rungRef.current = new Set()
            setElapsed(0)
        }

        setStatus('running')
    }

    const testBell = () => {
        unlockAudio()
        const context = audioRef.current
        if (context) strikeBell(context, context.currentTime + 0.05)
    }

    const active = status === 'running' || status === 'paused'
    const settling = active && elapsed < settleMs
    const remaining = settling ? settleMs - elapsed : totalMs - elapsed
    const progress = status === 'done'
        ? 1
        : active && !settling
            ? (elapsed - settleMs) / meditateMs
            : 0

    const radius = 46
    const circumference = 2 * Math.PI * radius

    const stateLabel =
        status === 'done'
            ? 'Done'
            : status === 'idle'
                ? 'Ready'
                : status === 'paused'
                    ? 'Paused'
                    : settling
                        ? 'Settling in'
                        : 'Meditating'

    const sessionSummary = settleSeconds > 0
        ? `${minutes} min, after ${settleSeconds === 60 ? 'a 1 minute' : 'a 30 second'} settle`
        : `${minutes} min`

    const pillClass = (selected: boolean) => cn(
        'rounded-full border px-3.5 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-border bg-background text-foreground hover:bg-secondary'
    )

    return (
        <section
            aria-label='Meditation timer'
            className='my-10 overflow-hidden rounded-2xl border border-border bg-card shadow-sm'
        >
            <div className='border-b border-border bg-secondary/50 px-5 py-4 sm:px-6'>
                <div className='flex items-center gap-2'>
                    <Timer className='size-5 shrink-0 text-primary' />
                    <span className='font-heading text-lg font-semibold tracking-tight text-foreground'>
                        Meditation timer
                    </span>
                </div>
                <span className='mt-1 block text-sm text-muted-foreground'>
                    Choose a length and a focus, press start, and close your eyes. A bell marks the start and the end.
                </span>
            </div>

            <div className='px-5 py-5 sm:px-6'>
                <fieldset disabled={active} className='flex flex-col gap-5 disabled:opacity-60'>
                    <div>
                        <span id='meditation-length' className='block text-sm font-medium text-foreground'>
                            Length
                        </span>
                        <span role='group' aria-labelledby='meditation-length' className='mt-2 flex flex-wrap gap-2'>
                            {DURATIONS.map((option) => (
                                <button
                                    key={option}
                                    type='button'
                                    aria-pressed={option === minutes}
                                    onClick={() => { setMinutes(option); reset() }}
                                    className={pillClass(option === minutes)}
                                >
                                    {option} min
                                </button>
                            ))}
                        </span>
                    </div>

                    <div>
                        <span id='meditation-anchor' className='block text-sm font-medium text-foreground'>
                            Focus on
                        </span>
                        <span role='group' aria-labelledby='meditation-anchor' className='mt-2 flex flex-wrap gap-2'>
                            {ANCHORS.map((item) => (
                                <button
                                    key={item.key}
                                    type='button'
                                    aria-pressed={item.key === anchor.key}
                                    onClick={() => setAnchorKey(item.key)}
                                    className={pillClass(item.key === anchor.key)}
                                >
                                    {item.name}
                                </button>
                            ))}
                        </span>
                    </div>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <div>
                            <label htmlFor='meditation-settle' className='block text-sm font-medium text-foreground'>
                                Time to settle before the bell
                            </label>
                            <select
                                id='meditation-settle'
                                value={settleSeconds}
                                onChange={(event) => { setSettleSeconds(Number(event.target.value)); reset() }}
                                className='mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                {SETTLE_OPTIONS.map((option) => (
                                    <option key={option.seconds} value={option.seconds}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label htmlFor='meditation-interval' className='block text-sm font-medium text-foreground'>
                                Soft reminder bell
                            </label>
                            <select
                                id='meditation-interval'
                                value={intervalMinutes}
                                onChange={(event) => { setIntervalMinutes(Number(event.target.value)); reset() }}
                                className='mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring'
                            >
                                {INTERVAL_OPTIONS.map((option) => (
                                    <option key={option.minutes} value={option.minutes}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </fieldset>

                <span className='mt-4 flex flex-wrap items-center gap-x-4 gap-y-2'>
                    <button
                        type='button'
                        aria-pressed={sound}
                        onClick={() => setSound((value) => !value)}
                        className='inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                    >
                        {sound ? <Bell className='size-4' /> : <BellOff className='size-4' />}
                        {sound ? 'Bells on' : 'Bells off'}
                    </button>
                    {sound && !active && (
                        <button
                            type='button'
                            onClick={testBell}
                            className='text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground'
                        >
                            Hear the bell
                        </button>
                    )}
                </span>

                <div className='mt-8 flex flex-col items-center'>
                    <div className='relative flex size-60 items-center justify-center sm:size-72'>
                        <svg viewBox='0 0 100 100' aria-hidden='true' className='absolute inset-0 size-full -rotate-90'>
                            <circle cx='50' cy='50' r={radius} fill='none' strokeWidth='3' className='stroke-secondary' />
                            <circle
                                cx='50'
                                cy='50'
                                r={radius}
                                fill='none'
                                strokeWidth='3'
                                strokeLinecap='round'
                                className='stroke-primary transition-[stroke-dashoffset] duration-300 ease-linear'
                                strokeDasharray={circumference}
                                strokeDashoffset={circumference * (1 - progress)}
                            />
                        </svg>
                        <span aria-hidden='true' className='absolute inset-[16%] rounded-full bg-primary/10' />

                        <span className='relative flex flex-col items-center text-center'>
                            <span aria-live='polite' className='text-sm font-medium uppercase tracking-wider text-muted-foreground'>
                                {stateLabel}
                            </span>
                            <span className='mt-1 font-heading text-5xl font-semibold tabular-nums tracking-tight text-foreground'>
                                {status === 'idle' ? formatClock(meditateMs) : status === 'done' ? formatClock(0) : formatClock(remaining)}
                            </span>
                            {settling && (
                                <span className='mt-1 text-xs text-muted-foreground'>until the first bell</span>
                            )}
                        </span>
                    </div>

                    <div className='mt-6 flex items-center gap-3'>
                        <button
                            type='button'
                            onClick={toggle}
                            className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                        >
                            {status === 'running' ? <Pause className='size-4' /> : <Play className='size-4' />}
                            {status === 'running'
                                ? 'Pause'
                                : status === 'paused'
                                    ? 'Resume'
                                    : status === 'done'
                                        ? 'Sit again'
                                        : 'Start'}
                        </button>

                        {status !== 'idle' && (
                            <button
                                type='button'
                                onClick={reset}
                                className='inline-flex items-center gap-2 rounded-full border border-border bg-background px-5 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                            >
                                <RotateCcw className='size-4' />
                                Reset
                            </button>
                        )}
                    </div>

                    <span className='mt-4 text-sm text-muted-foreground'>
                        {active ? `${anchor.name} focus, ${sessionSummary}` : sessionSummary}
                    </span>
                </div>

                {status !== 'done' && (
                    <div className='mt-6 rounded-xl border border-border bg-background px-4 py-4'>
                        <span className='block text-sm font-semibold text-foreground'>{anchor.prompt}</span>
                        <span className='mt-1 block text-sm leading-relaxed text-muted-foreground'>{anchor.wander}</span>
                    </div>
                )}

                {status === 'done' && (
                    <div className='mt-6 rounded-xl border border-primary/40 bg-primary/5 px-4 py-4'>
                        <span className='flex items-center gap-2 text-sm font-semibold text-foreground'>
                            <CircleCheck className='size-4 shrink-0 text-primary' />
                            Session complete: {minutes} {minutes === 1 ? 'minute' : 'minutes'} of practice
                        </span>
                        <span className='mt-2 block text-sm leading-relaxed text-muted-foreground'>
                            Before you get up, take a few seconds to notice how you feel compared with when you started.
                        </span>
                        <span className='mt-3 block text-sm text-muted-foreground'>Where to next:</span>
                        <span className='mt-1 flex flex-col gap-1 text-sm'>
                            <Link href='/meditation/what-research-says-meditation' className='font-medium text-primary no-underline hover:underline'>
                                What the research actually says about meditation
                            </Link>
                            <Link href='/breathwork/guided-breathing-timer' className='font-medium text-primary no-underline hover:underline'>
                                Try the guided breathing timer
                            </Link>
                            <Link href='/meditation' className='font-medium text-primary no-underline hover:underline'>
                                The complete guide to meditation
                            </Link>
                        </span>
                    </div>
                )}

                <div className='mt-6 flex gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-3'>
                    <Info className='mt-0.5 size-4 shrink-0 text-muted-foreground' />
                    <span className='text-xs leading-relaxed text-muted-foreground'>
                        If meditating brings up distress, anxiety or difficult memories, open your eyes and stop. That is a
                        normal choice, not a failure. Bells need your sound on; on some phones the silent switch mutes them.
                    </span>
                </div>
            </div>
        </section>
    )
}

export default MeditationTimer
